import { useStaticQuery, graphql, Link } from "gatsby"
import React from "react"
import styled from "styled-components"
import breakpoints from "../../styles/breakpoints"
import fonts from "../../styles/fonts"
import colors from "../../styles/colors"
import { getLocalisedContent } from "../../services/contentful"
import { useLocation } from "@reach/router"
import { withTranslation } from "gatsby-plugin-react-i18next"
import { getIsFrance } from "../../services/locale"
import {
  getBespokeLink,
  getEngagementPlpLink,
  getWeddingLink,
} from "../../services/links"

const NavigationContainer = styled.div`
  position: sticky;
  top: 120px;
  margin-top: 90px;
  padding: 70px 45px;
  display: none;
  max-height: calc(100vh - 120px);
  overflow-y: auto;

  .active {
    text-decoration: underline;
  }

  /* Hide scrollbar for Chrome, Safari and Opera */
  ::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  @media (min-width: ${breakpoints.lg}) {
    display: block;
  }
`

const LinkList = styled.ul`
  ${fonts.openSans};
  list-style: none;

  li {
    padding: 3px 0;
  }
`

const ColorList = styled.ul`
  margin-left: ${(props) => (props.$isSubcategory ? "0" : " 20px;")};
  height: ${(props) => (props.$visible ? "auto" : "0")};
  overflow: hidden;
  li {
    a {
      font-size: 14px;
      color: ${colors.mediumDarkGrey};
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }
  }
`

const SubCategoryList = styled.ul`
  margin-left: 20px;
  font-size: 14px;
  height: ${(props) => (props.$visible ? "auto" : "0")};
  overflow: hidden;
`

const SubCategoryLinkTitle = styled(Link)`
  color: ${colors.black};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`

const StyledLink = styled(Link)`
  ${fonts.openSans};
  font-weight: 600;
  font-size: 14px;
  color: ${(props) => (props.active ? colors.lightGreen : colors.black)};
  text-decoration: none;
  text-transform: uppercase;

  &:hover {
    color: ${colors.lightGreen};
  }
`

const DesktopNavigation = ({ t, engagement }) => {
  const { navigation } = useStaticQuery(
    graphql`
      query {
        navigation: allContentfulNavigation(
          filter: { navigationId: { eq: "plp-navigation" } }
        ) {
          nodes {
            node_locale
            desktopLeft {
              __typename
              ... on ContentfulLink {
                title
                link
                locale
                id
                isFeatured
                openBespokeForm
                externalLink
              }
              # ... on ContentfulLinkList {
              #   id
              #   header
              #   headerLink
              #   locale
              #   links {
              #     ... on ContentfulLink {
              #       __typename
              #       title
              #       link
              #       locale
              #       id
              #       isFeatured
              #       openBespokeForm
              #       externalLink
              #     }
              #   }
              # }
            }
            # desktopRight {
            #   __typename
            #   ... on ContentfulLink {
            #     title
            #     link
            #     locale
            #     id
            #     isFeatured
            #     openBespokeForm
            #     externalLink
            #   }
            # }
          }
        }
      }
    `
  )

  const localisedNavigation = getLocalisedContent(navigation.nodes)
  const activeEnv = process.env.GATSBY_ACTIVE_ENV || "us"
  const isFrance = getIsFrance()

  const location = useLocation()

  const fineJewelryMainLinks = [
    {
      title: t("plp.navigation.shop_all"),
      link: "/shop-jewelry/",
      links: [
        {
          title: t("plp.navigation.shop_all.yellow_gold"),
          link: isFrance
            ? "/shop-or-jaune-jewelry/"
            : "/shop-yellow-gold-jewelry/",
        },
        {
          title: t("plp.navigation.shop_all.white_gold"),
          link: isFrance
            ? "/shop-or-blanc-jewelry/"
            : "/shop-white-gold-jewelry/",
        },
        {
          title: t("plp.navigation.shop_all.rose_gold"),
          link: isFrance
            ? "/shop-or-rose-jewelry/"
            : "/shop-rose-gold-jewelry/",
        },
      ],
    },
    {
      title: t("plp.navigation.earrings"),
      link: t("link.earrings"),
      links: [
        {
          title: t("plp.navigation.earrings.yellow_gold"),
          link: isFrance
            ? "/or-jaune-boucles-d-oreilles/"
            : "/yellow-gold-earrings/",
        },
        {
          title: t("plp.navigation.earrings.white_gold"),
          link: isFrance
            ? "/or-blanc-boucles-d-oreilles/"
            : "/white-gold-earrings/",
        },
        {
          title: t("plp.navigation.earrings.rose_gold"),
          link: isFrance
            ? "/or-rose-boucles-d-oreilles/"
            : "/rose-gold-earrings/",
        },
      ],
      subCategories: [
        {
          title: t("plp.navigation.hoops"),
          link: t("link.hoops"),
          links: [
            {
              title: t("plp.navigation.hoops.yellow_gold"),
              link: isFrance ? "/or-jaune-creoles/" : "/yellow-gold-hoops/",
            },
            {
              title: t("plp.navigation.hoops.white_gold"),
              link: isFrance ? "/or-blanc-creoles/" : "/white-gold-hoops/",
            },
            {
              title: t("plp.navigation.hoops.rose_gold"),
              link: isFrance ? "/or-rose-creoles/" : "/rose-gold-hoops/",
            },
          ],
        },
        {
          title: t("plp.navigation.piercings"),
          link: t("link.piercings"),
          links: [
            {
              title: t("plp.navigation.piercings.yellow_gold"),
              link: isFrance
                ? "/or-jaune-piercings/"
                : "/yellow-gold-piercings/",
            },
            {
              title: t("plp.navigation.piercings.white_gold"),
              link: isFrance
                ? "/or-blanc-piercings/"
                : "/white-gold-piercings/",
            },
            {
              title: t("plp.navigation.piercings.rose_gold"),
              link: isFrance ? "/or-rose-piercings/" : "/rose-gold-piercings/",
            },
          ],
        },
        {
          title: t("plp.navigation.pairs"),
          link: t("link.pairs"),
          links: [
            {
              title: t("plp.navigation.pairs.yellow_gold"),
              link: isFrance ? "/or-jaune-paires/" : "/yellow-gold-pairs/",
            },
            {
              title: t("plp.navigation.pairs.white_gold"),
              link: isFrance ? "/or-blanc-paires/" : "/white-gold-pairs/",
            },
            {
              title: t("plp.navigation.pairs.rose_gold"),
              link: isFrance ? "/or-rose-paires/" : "/rose-gold-pairs/",
            },
          ],
        },
        isFrance
          ? null
          : {
              title: t("plp.navigation.ear_climbers"),
              link: t("link.ear_climbers"),
              links: [
                {
                  title: t("plp.navigation.ear_climbers.yellow_gold"),
                  link: "/yellow-gold-ear-climbers/",
                },
                {
                  title: t("plp.navigation.ear_climbers.white_gold"),
                  link: "/white-gold-ear-climbers/",
                },
                {
                  title: t("plp.navigation.ear_climbers.rose_gold"),
                  link: "/rose-gold-ear-climbers/",
                },
              ],
            },
        {
          title: t("plp.navigation.ear_cuffs"),
          link: t("link.ear_cuffs"),
          links: [
            {
              title: t("plp.navigation.ear_cuffs.yellow_gold"),
              link: isFrance
                ? "/or-jaune-ear-cuffs/"
                : "/yellow-gold-ear-cuffs/",
            },
            {
              title: t("plp.navigation.ear_cuffs.white_gold"),
              link: isFrance
                ? "/or-blanc-ear-cuffs/"
                : "/white-gold-ear-cuffs/",
            },
            {
              title: t("plp.navigation.ear_cuffs.rose_gold"),
              link: isFrance ? "/or-rose-ear-cuffs/" : "/rose-gold-ear-cuffs/",
            },
          ],
        },
        isFrance
          ? null
          : {
              title: t("plp.navigation.statement_earrings"),
              link: t("link.statement_earrings"),
              links: [
                {
                  title: t("plp.navigation.statement_earrings.yellow_gold"),
                  link: "/yellow-gold-statement-earrings/",
                },
                {
                  title: t("plp.navigation.statement_earrings.white_gold"),
                  link: "/white-gold-statement-earrings/",
                },
                {
                  title: t("plp.navigation.statement_earrings.rose_gold"),
                  link: "/rose-gold-statement-earrings/",
                },
              ],
            },
      ],
    },
    {
      title: t("plp.navigation.rings"),
      link: t("link.rings"),
      links: [
        {
          title: t("plp.navigation.rings.yellow_gold"),
          link: isFrance ? "/or-jaune-bagues/" : "/yellow-gold-rings/",
        },
        {
          title: t("plp.navigation.rings.white_gold"),
          link: isFrance ? "/or-blanc-bagues/" : "/white-gold-rings/",
        },
        {
          title: t("plp.navigation.rings.rose_gold"),
          link: isFrance ? "/or-rose-bagues/" : "/rose-gold-rings/",
        },
      ],
      subCategories: [
        isFrance
          ? null
          : {
              title: t("plp.navigation.statement_rings"),
              link: t("link.statement_rings"),
              links: [
                {
                  title: t("plp.navigation.statement_rings.yellow_gold"),
                  link: "/yellow-gold-statement-rings/",
                },
                {
                  title: t("plp.navigation.statement_rings.white_gold"),
                  link: "/white-gold-statement-rings/",
                },
                {
                  title: t("plp.navigation.statement_rings.rose_gold"),
                  link: "/rose-gold-statement-rings/",
                },
              ],
            },
        isFrance
          ? null
          : {
              title: t("plp.navigation.stackable_rings"),
              link: t("link.stackable_rings"),
              links: [
                {
                  title: t("plp.navigation.stackable_rings.yellow_gold"),
                  link: "/yellow-gold-stackable-rings/",
                },
                {
                  title: t("plp.navigation.stackable_rings.white_gold"),
                  link: "/white-gold-stackable-rings/",
                },
                {
                  title: t("plp.navigation.stackable_rings.rose_gold"),
                  link: "/rose-gold-stackable-rings/",
                },
              ],
            },
        isFrance
          ? null
          : {
              title: t("plp.navigation.pinky_rings"),
              link: t("link.pinky_rings"),
              links: [
                {
                  title: t("plp.navigation.pinky_rings.yellow_gold"),
                  link: "/yellow-gold-pinky-rings/",
                },
                {
                  title: t("plp.navigation.pinky_rings.white_gold"),
                  link: "/white-gold-pinky-rings/",
                },
                {
                  title: t("plp.navigation.pinky_rings.rose_gold"),
                  link: "/rose-gold-pinky-rings/",
                },
              ],
            },
        isFrance
          ? null
          : {
              title: t("plp.navigation.eternity_rings"),
              link: t("link.eternity_rings"),
              links: [
                {
                  title: t("plp.navigation.eternity_rings.yellow_gold"),
                  link: "/yellow-gold-eternity-rings/",
                },
                {
                  title: t("plp.navigation.eternity_rings.white_gold"),
                  link: "/white-gold-eternity-rings/",
                },
                {
                  title: t("plp.navigation.eternity_rings.rose_gold"),
                  link: "/rose-gold-eternity-rings/",
                },
              ],
            },
      ],
    },
    {
      title: t("plp.navigation.necklaces"),
      link: t("link.necklaces"),
      links: [
        {
          title: t("plp.navigation.necklaces.yellow_gold"),
          link: isFrance ? "/or-jaune-colliers/" : "/yellow-gold-necklaces/",
        },
        {
          title: t("plp.navigation.necklaces.white_gold"),
          link: isFrance ? "/or-blanc-colliers/" : "/white-gold-necklaces/",
        },
        {
          title: t("plp.navigation.necklaces.rose_gold"),
          link: isFrance ? "/or-rose-colliers/" : "/rose-gold-necklaces/",
        },
      ],
      subCategories: [
        {
          title: t("plp.navigation.personalized_necklaces"),
          link: t("link.personalized_necklaces"),
          links: [
            {
              title: t("plp.navigation.personalized_necklaces.yellow_gold"),
              link: isFrance
                ? "/or-jaune-colliers-personnalises/"
                : "/yellow-gold-personalized-necklaces/",
            },
            {
              title: t("plp.navigation.personalized_necklaces.white_gold"),
              link: isFrance
                ? "/or-blanc-colliers-personnalises/"
                : "/white-gold-personalized-necklaces/",
            },
            {
              title: t("plp.navigation.personalized_necklaces.rose_gold"),
              link: isFrance
                ? "/or-rose-colliers-personnalises/"
                : "/rose-gold-personalized-necklaces/",
            },
          ],
        },
        isFrance
          ? null
          : {
              title: t("plp.navigation.statement_necklaces"),
              link: t("link.statement_necklaces"),
              links: [
                {
                  title: t("plp.navigation.statement_necklaces.yellow_gold"),
                  link: "/yellow-gold-statement-necklaces/",
                },
                {
                  title: t("plp.navigation.statement_necklaces.white_gold"),
                  link: "/white-gold-statement-necklaces/",
                },
                {
                  title: t("plp.navigation.statement_necklaces.rose_gold"),
                  link: "/rose-gold-statement-necklaces/",
                },
              ],
            },
        {
          title: t("plp.navigation.solitaire_necklaces"),
          link: t("link.solitaire_necklaces"),
          links: [
            {
              title: t("plp.navigation.solitaire_necklaces.yellow_gold"),
              link: isFrance
                ? "/or-jaune-colliers-solitaire/"
                : "/yellow-gold-solitaire-necklaces/",
            },
            {
              title: t("plp.navigation.solitaire_necklaces.white_gold"),
              link: isFrance
                ? "/or-blanc-colliers-solitaire/"
                : "/white-gold-solitaire-necklaces/",
            },
            {
              title: t("plp.navigation.solitaire_necklaces.rose_gold"),
              link: isFrance
                ? "/or-rose-colliers-solitaire/"
                : "/rose-gold-solitaire-necklaces/",
            },
          ],
        },
        isFrance
          ? null
          : {
              title: t("plp.navigation.layerable_necklaces"),
              link: t("link.layerable_necklaces"),
              links: [
                {
                  title: t("plp.navigation.layerable_necklaces.yellow_gold"),
                  link: "/yellow-gold-layerable-necklaces/",
                },
                {
                  title: t("plp.navigation.layerable_necklaces.white_gold"),
                  link: "/white-gold-layerable-necklaces/",
                },
                {
                  title: t("plp.navigation.layerable_necklaces.rose_gold"),
                  link: "/rose-gold-layerable-necklaces/",
                },
              ],
            },
      ],
    },
    {
      title: t("plp.navigation.bracelets"),
      link: t("link.bracelets"),
      links: [
        {
          title: t("plp.navigation.bracelets.yellow_gold"),
          link: isFrance ? "/or-jaune-bracelets/" : "/yellow-gold-bracelets/",
        },
        {
          title: t("plp.navigation.bracelets.white_gold"),
          link: isFrance ? "/or-blanc-bracelets/" : "/white-gold-bracelets/",
        },
        {
          title: t("plp.navigation.bracelets.rose_gold"),
          link: isFrance ? "/or-rose-bracelets/" : "/rose-gold-bracelets/",
        },
      ],
      subCategories: [
        {
          title: t("plp.navigation.personalized_bracelets"),
          link: t("link.personalized_bracelets"),
          links: [
            {
              title: t("plp.navigation.personalized_bracelets.yellow_gold"),
              link: isFrance
                ? "/or-jaune-bracelets-personnalises"
                : "/yellow-gold-personalized-bracelets/",
            },
            {
              title: t("plp.navigation.personalized_bracelets.white_gold"),
              link: isFrance
                ? "/or-blanc-bracelets-personnalises"
                : "/white-gold-personalized-bracelets/",
            },
            {
              title: t("plp.navigation.personalized_bracelets.rose_gold"),
              link: isFrance
                ? "/or-rose-bracelets-personnalises"
                : "/rose-gold-personalized-bracelets/",
            },
          ],
        },
        isFrance
          ? null
          : {
              title: t("plp.navigation.bangle_bracelets"),
              link: t("link.bangle_bracelets"),
              links: [
                {
                  title: t("plp.navigation.bangle_bracelets.yellow_gold"),
                  link: "/yellow-gold-bangle-bracelets/",
                },
                {
                  title: t("plp.navigation.bangle_bracelets.white_gold"),
                  link: "/white-gold-bangle-bracelets/",
                },
                {
                  title: t("plp.navigation.bangle_bracelets.rose_gold"),
                  link: "/rose-gold-bangle-bracelets/",
                },
              ],
            },
        {
          title: t("plp.navigation.cord_bracelets"),
          link: t("link.cord_bracelets"),
          links: [
            {
              title: t("plp.navigation.cord_bracelets.yellow_gold"),
              link: isFrance
                ? "/or-jaune-bracelets-corde/"
                : "/yellow-gold-cord-bracelets/",
            },
            {
              title: t("plp.navigation.cord_bracelets.white_gold"),
              link: isFrance
                ? "/or-blanc-bracelets-corde/"
                : "/white-gold-cord-bracelets/",
            },
            {
              title: t("plp.navigation.cord_bracelets.rose_gold"),
              link: isFrance
                ? "/or-rose-bracelets-corde/"
                : "/rose-gold-cord-bracelets/",
            },
          ],
        },
        {
          title: t("plp.navigation.bracelets_for_him"),
          link: t("link.bracelets_for_him"),
          links: [
            {
              title: t("plp.navigation.bracelets_for_him.yellow_gold"),
              link: isFrance
                ? "/or-jaune-bracelets-pour-lui/"
                : "/yellow-gold-bracelets-for-him/",
            },
            {
              title: t("plp.navigation.bracelets_for_him.white_gold"),
              link: isFrance
                ? "/or-blanc-bracelets-pour-lui/"
                : "/white-gold-bracelets-for-him/",
            },
            {
              title: t("plp.navigation.bracelets_for_him.rose_gold"),
              link: isFrance
                ? "/or-rose-bracelets-pour-lui/"
                : "/rose-gold-bracelets-for-him/",
            },
          ],
        },
      ],
    },
    {
      title: t("plp.navigation.mens_collection"),
      link: t("link.mens_collection"),
      links: [
        {
          title: t("plp.navigation.mens_collection.yellow_gold"),
          link: isFrance ? "/or-jaune-bracelets-pour-lui/" : "/yellow-gold-bracelets-for-him/",
        },
        {
          title: t("plp.navigation.mens_collection.white_gold"),
          link: isFrance ? "/or-blanc-bracelets-pour-lui/" : "/white-gold-bracelets-for-him/",
        },
        {
          title: t("plp.navigation.mens_collection.rose_gold"),
          link: isFrance ? "/or-rose-bracelets-pour-lui/" : "/rose-gold-bracelets-for-him/",
        },
      ],
    },
    {
      title: t("plp.navigation.shop_engagement"),
      link: getEngagementPlpLink(),
    },
    {
      title: t("plp.navigation.bespoke_service"),
      link: getBespokeLink(),
    },
  ]

  const engagementMainLinks = [
    { title: t("plp.navigation.shop_all"), link: "/shop-jewelry/" },
    {
      title: t("plp.navigation.shop_engagement"),
      link: getEngagementPlpLink(),
      links: [
        {
          title: t("plp.navigation.shop_engagement.yellow_gold"),
          link: isFrance
            ? "/or-jaune-bague-de-fiancailles/"
            : "/yellow-gold-engagement-rings/",
        },
        {
          title: t("plp.navigation.shop_engagement.white_gold"),
          link: isFrance
            ? "/or-blanc-bague-de-fiancailles/"
            : "/white-gold-engagement-rings/",
        },
        {
          title: t("plp.navigation.shop_engagement.rose_gold"),
          link: isFrance
            ? "/or-rose-bague-de-fiancailles/"
            : "/rose-gold-engagement-rings/",
        },
      ],
      subCategories: [
        {
          title: t("plp.navigation.wedding_bands_for_her"),
          link: getWeddingLink(),
          links: [
            {
              title: t("plp.navigation.wedding_bands_for_her.yellow_gold"),
              link: isFrance
                ? "/or-jaune-alliances/"
                : "/yellow-gold-wedding-rings-bands/",
            },
            {
              title: t("plp.navigation.wedding_bands_for_her.white_gold"),
              link: isFrance
                ? "/or-blanc-alliances/"
                : "/white-gold-wedding-rings-bands/",
            },
            {
              title: t("plp.navigation.wedding_bands_for_her.rose_gold"),
              link: isFrance
                ? "/or-rose-alliances/"
                : "/rose-gold-wedding-rings-bands/",
            },
          ],
        },
        {
          title: t("plp.navigation.wedding_bands_for_him"),
          link: isFrance
            ? "/alliances-pour-lui/"
            : "/wedding-bands-for-him/",
          links: [
            {
              title: t("plp.navigation.wedding_bands_for_him.yellow_gold"),
              link: isFrance
                ? "/or-jaune-alliances-pour-lui/"
                : "/yellow-gold-wedding-rings-bands-for-him/",
            },
            {
              title: t("plp.navigation.wedding_bands_for_him.white_gold"),
              link: isFrance
                ? "/or-blanc-alliances-pour-lui/"
                : "/white-gold-wedding-rings-bands-for-him/",
            },
            {
              title: t("plp.navigation.wedding_bands_for_him.rose_gold"),
              link: isFrance
                ? "/or-rose-alliances-pour-lui/"
                : "/rose-gold-wedding-rings-bands-for-him/",
            },
          ],
        },
        {
          title: t("plp.navigation.bridal_collection"),
          link: isFrance ? "/mariage/" : "/wedding-day/",
        },
      ],
    },
    {
      title: t("plp.navigation.bespoke_service"),
      link: getBespokeLink(),
    },
  ]

  const mainLinks = engagement ? engagementMainLinks : fineJewelryMainLinks

  const pathname =
    activeEnv === "us"
      ? location.pathname
      : location.pathname.replace(`/${activeEnv}/`, "/")

  return (
    <NavigationContainer>
      <LinkList>
        {localisedNavigation[0]?.desktopLeft.filter(link => link.__typename === "ContentfulLink").map((link, index) => {
          const isVisible = link.locale.includes(activeEnv.toUpperCase())
          const slug = link.link.substring(1)
          const lowerCaseTitle = link.title.toLowerCase()
          const nonPLPs = [
            "product",
            "produit",
            "bespoke-engagement-ring",
            "service-sur-measure",
          ]
          const colorLinks = nonPLPs.some((seg) => slug.includes(seg))
            ? []
            : [
                {
                  title: isFrance
                    ? `${link.title} en or jaune`
                    : `Yellow gold ${lowerCaseTitle}`,
                  link: `${isFrance ? "/or-jaune-" : "/yellow-gold-"}${slug}`,
                },
                {
                  title: isFrance
                    ? `${link.title} en or jaune`
                    : `White gold ${lowerCaseTitle}`,
                  link: `${isFrance ? "/or-blanc-" : "/white-gold-"}${slug}`,
                },
                {
                  title: isFrance
                    ? `${link.title} en or jaune`
                    : `Rose gold ${lowerCaseTitle}`,
                  link: `${isFrance ? "/or-rose-" : "/rose-gold-"}${slug}`,
                },
              ]
          const isOpen =
            link.link?.includes(pathname) ||
            colorLinks.some((colorLink) => colorLink.link.includes(pathname))
          return isVisible ? (
            <li key={`${link.link}-${index}`}>
              <StyledLink to={link.link} active={isOpen ? 1 : 0}>
                {link.title}
              </StyledLink>
              <ColorList $visible={isOpen}>
                {colorLinks.map((link, i) => {
                  return (
                    <li key={`${link.link}-${i}`}>
                      <Link to={link.link} activeClassName="active">
                        {link.title}
                      </Link>
                    </li>
                  )
                })}
              </ColorList>
            </li>
          ) : null
        })}
      </LinkList>
      <LinkList>
        {mainLinks.map((menu, index) => {
          if (!menu) return null
          const isOpen =
            menu?.link?.includes(pathname) ||
            menu?.links?.some((colorLink) =>
              colorLink.link.includes(pathname) ? true : false
            )
          const subCategoryListVisible =
            isOpen ||
            menu?.subCategories?.some(
              (subCategory) =>
                subCategory?.link?.includes(pathname) ||
                subCategory?.links?.some((subCategoryColor) =>
                  subCategoryColor.link.includes(pathname)
                )
            )
          return (
            <li key={index}>
              <StyledLink
                to={menu.link}
                active={isOpen || subCategoryListVisible ? 1 : 0}
              >
                {menu.title}
              </StyledLink>
              {menu.links && (
                <ColorList $visible={isOpen}>
                  {menu.links.map((link, i) => {
                    return (
                      <li key={`${link.link}-${i}`}>
                        <Link to={link.link} activeClassName="active">
                          {link.title}
                        </Link>
                      </li>
                    )
                  })}
                </ColorList>
              )}
              {menu.subCategories && (
                <SubCategoryList $visible={subCategoryListVisible}>
                  {menu.subCategories.map((subCategory, c) => {
                    if (!subCategory) return null
                    const isOpen =
                      subCategory?.link?.includes(pathname) ||
                      subCategory?.links?.some((subCategoryColor) =>
                        subCategoryColor.link.includes(pathname)
                      )
                    return (
                      <li key={`${subCategory.link}-${c}`}>
                        <SubCategoryLinkTitle
                          to={subCategory.link}
                          activeClassName="active"
                        >
                          {subCategory.title}
                        </SubCategoryLinkTitle>
                        {subCategory.links && (
                          <ColorList $isSubcategory={1} $visible={isOpen}>
                            {subCategory.links.map(
                              (subCategoryColorLink, cl) => {
                                return (
                                  <li
                                    key={`${subCategoryColorLink.link}-${cl}`}
                                  >
                                    <Link
                                      to={subCategoryColorLink.link}
                                      activeClassName="active"
                                    >
                                      {subCategoryColorLink.title}
                                    </Link>
                                  </li>
                                )
                              }
                            )}
                          </ColorList>
                        )}
                      </li>
                    )
                  })}
                </SubCategoryList>
              )}
            </li>
          )
        })}
      </LinkList>
      <LinkList>
        {localisedNavigation[0]?.desktopLeft.filter(link => link.__typename === "ContentfulLinkList").map((link, index) => {
          const isVisible = link.locale.includes(activeEnv.toUpperCase())
          const isOpen =
            link.headerLink?.includes(pathname) ||
            link.links.some((innerLink) => innerLink.link.includes(pathname))
          
          return isVisible ? (
            <li key={`${link.link}-${index}`}>
              <StyledLink to={link.headerLink} active={isOpen? 1 : 0}>
                {link.header}
              </StyledLink>
              <ColorList $visible={isOpen}>
                {link.links.filter(link => link.locale.includes(activeEnv.toUpperCase())).map((innerLink, i) => {
                  return (
                    <li key={`${innerLink.link}-${i}`}>
                      <Link to={innerLink.link} activeClassName="active">
                        {innerLink.title}
                      </Link>
                    </li>
                  )
                })}
              </ColorList>
            </li>
          ) : null
        })}
        {localisedNavigation[0]?.desktopRight?.map((link, index) => {
          let isVisible = false;

          if (typeof link.locale !== "undefined") {
            isVisible = link.locale.includes(activeEnv.toUpperCase());
          }

          return isVisible ? (
            <li key={`${link.link}-${index}`}>
              <StyledLink to={link.link} activeClassName="active">
                {link.title}
              </StyledLink>
            </li>
          ) : null
        })}
      </LinkList>
    </NavigationContainer>
  )
}
export default withTranslation()(DesktopNavigation)
